'use client';

import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { Controller, type FieldError, useForm } from 'react-hook-form';

import { twMerge } from '@/tailwind-merge.config';

export interface ComplaintArticleData {
  id: string;
  isSelected: boolean;
  position: string;
  quantity: string;
  originalQuantity: string;
  model: string;
  articleNumber: string;
  variant1: string;
  variant2: string;
  variant3: string;
  variant4: string;
  variant5: string;
  articleDescription: string;
  errorDescription: string;
  priceReductionWished: boolean;
  completedByCustomer: boolean;
}

interface ComplaintArticleRowProps {
  initialData?: Partial<ComplaintArticleData>;
  isSelected: boolean;
  externalErrors?: string[];
  onSelectionChange: (selected: boolean) => void;
  onChange?: (data: ComplaintArticleData) => void;
}

export function ComplaintArticleRow({
  initialData,
  isSelected,
  externalErrors,
  onSelectionChange,
  onChange,
}: ComplaintArticleRowProps) {
  const { t } = useTranslation('common');
  const {
    control,
    watch,
    getValues,
    reset,
    formState: { errors },
  } = useForm<ComplaintArticleData>({
    mode: 'onChange',
    defaultValues: {
      id: initialData?.id || '',
      isSelected: initialData?.isSelected || false,
      position: initialData?.position || '',
      quantity: initialData?.quantity || '',
      originalQuantity: initialData?.originalQuantity || '',
      model: initialData?.model || '',
      articleNumber: initialData?.articleNumber || '',
      variant1: initialData?.variant1 || '',
      variant2: initialData?.variant2 || '',
      variant3: initialData?.variant3 || '',
      variant4: initialData?.variant4 || '',
      variant5: initialData?.variant5 || '',
      articleDescription: initialData?.articleDescription || '',
      errorDescription: initialData?.errorDescription || '',
      priceReductionWished: initialData?.priceReductionWished || false,
      completedByCustomer: initialData?.completedByCustomer || false,
    },
  });

  // Reset form when initialData changes
  useEffect(() => {
    reset({
      id: initialData?.id || '',
      isSelected: initialData?.isSelected || false,
      position: initialData?.position || '',
      quantity: initialData?.quantity || '',
      originalQuantity: initialData?.originalQuantity || '',
      model: initialData?.model || '',
      articleNumber: initialData?.articleNumber || '',
      variant1: initialData?.variant1 || '',
      variant2: initialData?.variant2 || '',
      variant3: initialData?.variant3 || '',
      variant4: initialData?.variant4 || '',
      variant5: initialData?.variant5 || '',
      articleDescription: initialData?.articleDescription || '',
      errorDescription: initialData?.errorDescription || '',
      priceReductionWished: initialData?.priceReductionWished || false,
      completedByCustomer: initialData?.completedByCustomer || false,
    });
  }, [initialData, reset]);

  // Watch all form values and trigger onChange callback
  useEffect(() => {
    const subscription = watch(() => {
      onChange?.(getValues());
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange, getValues]);

  // Helper function to render field errors
  const renderError = (error: FieldError | undefined) => {
    if (!error) return null;
    return <p className={errorTextStyles}>{error.message}</p>;
  };

  // Shared input styles
  const inputBaseStyles = twMerge(
    'w-full border border-black px-3 py-2 text-p1CopyMobile lg:text-p1CopyDesktop',
    'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-black'
  );

  const inputErrorStyles = 'border-inperspective-rot border-[3px]';
  const labelStyles =
    'block text-p2CopyMobile lg:text-p2CopyDesktop font-normal mb-2 uppercase tracking-[0.05em]';
  const errorTextStyles =
    'text-inperspective-rot text-p2CopyMobile lg:text-p2CopyDesktop mt-1';

  const hasExternalErrors = externalErrors && externalErrors.length > 0;

  return (
    <div
      className={twMerge(
        'flex gap-4 border-b border-gray-300 pb-8',
        hasExternalErrors && 'rounded-lg border-2 border-inperspective-rot p-4'
      )}
    >
      {/* Selection Checkbox */}
      <div className="flex basis-32 flex-col items-center">
        <label
          htmlFor={`article-selection-${initialData?.id}`}
          className={twMerge(labelStyles, 'mb-4 text-center')}
        >
          {t('complaint.articles.select_for_complaint')}
        </label>
        <input
          id={`article-selection-${initialData?.id}`}
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelectionChange(e.target.checked)}
          className="size-5 cursor-pointer rounded border-2 border-gray-400 text-black focus:ring-2 focus:ring-black"
          aria-label={t('complaint.articles.select_article')}
        />
      </div>

      {/* Form Content */}
      <div className="flex-1 space-y-6">
        {/* Main grid for article data */}
        <div className="grid grid-cols-12 gap-4">
          {/* Position */}
          <Controller
            name="position"
            control={control}
            rules={{ required: t('complaint.validation.field_required') }}
            render={({ field }) => (
              <div className="col-span-12 tablet:col-span-1 tablet:col-start-1">
                <label htmlFor={field.name} className={labelStyles}>
                  {t('complaint.fields.position')} *
                </label>
                <input
                  {...field}
                  id={field.name}
                  type="text"
                  disabled
                  className={twMerge(
                    inputBaseStyles,
                    errors.position && inputErrorStyles,
                    'cursor-not-allowed bg-gray-50'
                  )}
                />
                {renderError(errors.position)}
              </div>
            )}
          />

          {/* Menge (Quantity) */}
          <Controller
            name="quantity"
            control={control}
            rules={{
              required: t('complaint.validation.quantity_required'),
              validate: (value) => {
                const max = Number(initialData?.originalQuantity);
                const num = Number(value);
                if (isNaN(num)) return t('complaint.validation.numbers_only');
                if (!value || num <= 0)
                  return t('complaint.validation.quantity_min');
                if (!isNaN(max) && num > max)
                  return t('complaint.validation.quantity_exceeds_order', {
                    max,
                  });
                return true;
              },
            }}
            render={({ field }) => (
              <div className="col-span-12 tablet:col-span-1">
                <label htmlFor={field.name} className={labelStyles}>
                  {t('complaint.fields.quantity')} *
                </label>
                <input
                  {...field}
                  id={field.name}
                  type="number"
                  min="1"
                  max={initialData?.originalQuantity}
                  disabled={!isSelected}
                  className={twMerge(
                    inputBaseStyles,
                    errors.quantity && inputErrorStyles,
                    !isSelected && 'cursor-not-allowed bg-gray-50'
                  )}
                />
                {renderError(errors.quantity)}
              </div>
            )}
          />

          {/* Modell */}
          <Controller
            name="model"
            control={control}
            render={({ field }) => (
              <div className="col-span-12 tablet:col-span-2">
                <label htmlFor={field.name} className={labelStyles}>
                  {t('complaint.fields.model')}
                </label>
                <input
                  {...field}
                  id={field.name}
                  type="text"
                  disabled
                  className={twMerge(
                    inputBaseStyles,
                    'cursor-not-allowed bg-gray-50'
                  )}
                />
              </div>
            )}
          />

          {/* Artikelzeichen */}
          <Controller
            name="articleNumber"
            control={control}
            render={({ field }) => (
              <div className="col-span-12 tablet:col-span-2">
                <label htmlFor={field.name} className={labelStyles}>
                  {t('complaint.fields.article_code')}
                </label>
                <input
                  {...field}
                  id={field.name}
                  type="text"
                  disabled
                  className={twMerge(
                    inputBaseStyles,
                    'cursor-not-allowed bg-gray-50'
                  )}
                />
              </div>
            )}
          />

          {/* Variante fields (5 small inputs) */}
          <div className="col-span-12 flex gap-2 tablet:col-span-3">
            <Controller
              name="variant1"
              control={control}
              render={({ field }) => (
                <div className="flex-1">
                  <label htmlFor={field.name} className={labelStyles}>
                    {t('complaint.fields.variant')}
                  </label>
                  <input
                    {...field}
                    id={field.name}
                    type="text"
                    disabled
                    className={twMerge(
                      inputBaseStyles,
                      'cursor-not-allowed bg-gray-50'
                    )}
                  />
                </div>
              )}
            />
            <Controller
              name="variant2"
              control={control}
              render={({ field }) => (
                <div className="flex-1">
                  <label htmlFor={field.name} className="sr-only">
                    Variante 2
                  </label>
                  <input
                    {...field}
                    id={field.name}
                    type="text"
                    disabled
                    className={twMerge(
                      inputBaseStyles,
                      'mt-[26px]',
                      'cursor-not-allowed bg-gray-50'
                    )}
                  />
                </div>
              )}
            />
            <Controller
              name="variant3"
              control={control}
              render={({ field }) => (
                <div className="flex-1">
                  <label htmlFor={field.name} className="sr-only">
                    Variante 3
                  </label>
                  <input
                    {...field}
                    id={field.name}
                    type="text"
                    disabled
                    className={twMerge(
                      inputBaseStyles,
                      'mt-[26px]',
                      'cursor-not-allowed bg-gray-50'
                    )}
                  />
                </div>
              )}
            />
            <Controller
              name="variant4"
              control={control}
              render={({ field }) => (
                <div className="flex-1">
                  <label htmlFor={field.name} className="sr-only">
                    Variante 4
                  </label>
                  <input
                    {...field}
                    id={field.name}
                    type="text"
                    disabled
                    className={twMerge(
                      inputBaseStyles,
                      'mt-[26px]',
                      'cursor-not-allowed bg-gray-50'
                    )}
                  />
                </div>
              )}
            />
            <Controller
              name="variant5"
              control={control}
              render={({ field }) => (
                <div className="flex-1">
                  <label htmlFor={field.name} className="sr-only">
                    Variante 5
                  </label>
                  <input
                    {...field}
                    id={field.name}
                    type="text"
                    disabled
                    className={twMerge(
                      inputBaseStyles,
                      'mt-[26px]',
                      'cursor-not-allowed bg-gray-50'
                    )}
                  />
                </div>
              )}
            />
          </div>
        </div>

        {/* Article Description - Full width */}
        <Controller
          name="articleDescription"
          control={control}
          render={({ field }) => (
            <div className="grid grid-cols-12 gap-0">
              <div className="col-span-12 tablet:col-start-1">
                <input
                  {...field}
                  id={field.name}
                  type="text"
                  disabled
                  className={twMerge(
                    inputBaseStyles,
                    'cursor-not-allowed bg-gray-50'
                  )}
                />
              </div>
            </div>
          )}
        />

        {/* Error Description - Only visible when selected */}
        {isSelected && (
          <Controller
            name="errorDescription"
            control={control}
            rules={{ required: t('complaint.validation.field_required') }}
            render={({ field }) => (
              <div>
                <label htmlFor={field.name} className={labelStyles}>
                  {t('complaint.fields.error_description')} *{' '}
                  {errors.errorDescription && (
                    <span className="font-semibold">
                      {t('complaint.validation.field_required')}
                    </span>
                  )}
                </label>
                <textarea
                  {...field}
                  id={field.name}
                  className={twMerge(
                    inputBaseStyles,
                    'min-h-[100px] resize-y',
                    errors.errorDescription && inputErrorStyles
                  )}
                />
              </div>
            )}
          />
        )}

        {/* Checkboxes - Only visible when selected */}
        {isSelected && (
          <div className="flex items-center gap-8">
            <Controller
              name="priceReductionWished"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <div className="flex items-center gap-2">
                  <input
                    {...field}
                    id={`${field.name}-${initialData?.id}`}
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    className="size-4.5 border border-black"
                  />
                  <label
                    htmlFor={`${field.name}-${initialData?.id}`}
                    className="text-p2CopyMobile uppercase tracking-wider lg:text-p2CopyDesktop"
                  >
                    {t('complaint.fields.price_reduction')}
                  </label>
                </div>
              )}
            />

            <Controller
              name="completedByCustomer"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <div className="flex items-center gap-2">
                  <input
                    {...field}
                    id={`${field.name}-${initialData?.id}`}
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    className="size-4.5 border border-black checked:bg-palmberg-grn"
                  />
                  <label
                    htmlFor={`${field.name}-${initialData?.id}`}
                    className="text-p2CopyMobile uppercase tracking-wider lg:text-p2CopyDesktop"
                  >
                    {t('complaint.fields.completed_by_customer')}
                  </label>
                </div>
              )}
            />
          </div>
        )}

        {/* External Errors */}
        {hasExternalErrors && (
          <div className="space-y-1">
            {externalErrors.map((error, index) => (
              <p key={index} className={errorTextStyles}>
                {error}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
